import { COLORS, TYPOGRAPHY } from '@app/constants';
import { useOnClickOutside } from '@app/hooks/useOnClickOutside';
import { Arrow2, ArrowDown } from '@app/ui/iconComponents';
import { dateRangeLess } from '@app/utils/dateRangeLess';
import { dateRange } from '@app/utils/dateRange';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import { FC, useRef, useState } from 'react';
import { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import styled from 'styled-components';
import { isThisYear } from 'date-fns';
import Button from '@app/ui/components/Button';
import { BreakpointsEnum } from '@/app/types';

const months = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

interface Props extends ReactDatePickerCustomHeaderProps {
  className?: string;
  pastYear?: number;
  isPastYears?: boolean;
  numberOfYearsAgo?: number;
  hasResetButton?: boolean;
  onReset?: () => void;
}

const Header: FC<Props> = ({
  className,
  pastYear = new Date().getFullYear(),
  date,
  numberOfYearsAgo = 100,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  isPastYears,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  hasResetButton = false,
  onReset,
}) => {
  const currentMonth = months[getMonth(date)];
  const currentYear = getYear(date);
  const monthWithYearWrapperRef = useRef(null);
  const [isOpenYearsDropdown, setIsOpenYearsDropdown] = useState(false);

  useOnClickOutside(monthWithYearWrapperRef, () =>
    setIsOpenYearsDropdown(false),
  );

  const yearsRange = isPastYears
    ? dateRangeLess(
        new Date().getFullYear(),
        new Date().getFullYear() - numberOfYearsAgo,
      )
    : dateRange(pastYear);

  return (
    <Root className={className} $hasResetButton={hasResetButton}>
      <NavigationWrap>
        <PrevNavigationBtn
          onClick={decreaseMonth}
          type="button"
          disabled={prevMonthButtonDisabled}
        >
          <StyledArrow />
        </PrevNavigationBtn>
        <MonthWithYearWrapper ref={monthWithYearWrapperRef}>
          <MonthWithYearButton
            type="button"
            onClick={() => setIsOpenYearsDropdown(!isOpenYearsDropdown)}
          >
            <MonthWithYear>
              {currentMonth}, {currentYear}
            </MonthWithYear>
            <MonthWithYearArrow $isActive={isOpenYearsDropdown} />
          </MonthWithYearButton>
          {isOpenYearsDropdown && (
            <YearsDropdown>
              {yearsRange.map((item) => {
                return (
                  <YearButton
                    key={item.toString()}
                    onClick={() => {
                      changeYear(item);
                      if (
                        getMonth(date) < getMonth(new Date()) &&
                        isThisYear(new Date().setFullYear(item))
                      ) {
                        changeMonth(getMonth(new Date()));
                      }
                      setIsOpenYearsDropdown(false);
                    }}
                  >
                    {item}
                  </YearButton>
                );
              })}
            </YearsDropdown>
          )}
        </MonthWithYearWrapper>
        <NavigationBtn
          onClick={increaseMonth}
          type="button"
          disabled={
            nextMonthButtonDisabled ||
            (currentYear === yearsRange[yearsRange.length - 1] &&
              currentMonth === months[months.length - 1])
          }
        >
          <StyledArrow />
        </NavigationBtn>
      </NavigationWrap>
      {hasResetButton && (
        <StyledResetButton type="button" styleType="tertiary" onClick={onReset}>
          Сбросить
        </StyledResetButton>
      )}
    </Root>
  );
};

const Root = styled.div<{ $hasResetButton: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $hasResetButton }) =>
    $hasResetButton ? 'space-between' : 'center'};
  margin-bottom: 16px;
  padding-right: 3px;

  @media (min-width: ${BreakpointsEnum.md}px) {
    padding-left: 12px;
  }
`;

const StyledResetButton = styled(Button)`
  padding: 6px 17px;
`;

const NavigationWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledArrow = styled(Arrow2)`
  width: 12px;
  height: 12px;
`;

const NavigationBtn = styled.button`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  :disabled {
    cursor: auto;
    opacity: 0.5;
  }
`;

const PrevNavigationBtn = styled(NavigationBtn)`
  ${StyledArrow} {
    transform: rotate(180deg);
  }
`;

const MonthWithYearWrapper = styled.div`
  display: flex;
  justify-content: center;
  min-width: 131px;
  margin-left: 16px;
  margin-right: 16px;
  position: relative;
`;

const MonthWithYearButton = styled.button`
  display: flex;
  align-items: center;
`;

const MonthWithYear = styled.p`
  ${TYPOGRAPHY.headline2Medium16};
  color: ${COLORS.grayscale600};
  margin-right: 4px;
  min-width: 95px;
`;

const MonthWithYearArrow = styled(ArrowDown)<{ $isActive: boolean }>`
  transform: ${({ $isActive }) => $isActive && 'rotate(180deg)'};
`;

const YearsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  overflow: auto;
  width: 200px;
  height: 176px;
  background-color: ${COLORS.white};
  box-shadow: 0px 8px 24px rgba(75, 100, 137, 0.1);
  border-radius: 10px;
  z-index: 1;
`;

const YearButton = styled.button`
  ${TYPOGRAPHY.body1Regular14};
  color: ${COLORS.grayscale700};
  padding: 8px 16px;
  width: 100%;
  transition: background-color 0.3s ease-in;
  text-align: left;
  &:hover,
  &:focus {
    background-color: ${COLORS.primary50};
  }
`;

export default Header;
